import axios from "./axios";

export async function deleteOfferCalculation(payload) {
  return axios
    .delete(`/offer_calculations/` + payload)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function createOfferCalculation(payload) {
  return axios
    .post(`/offer_calculations/`, { offer_calculation: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) =>
      normalizeCalculationApiError(error, "Failed to create calculation")
    );
}

export async function updateOfferCalculation(payload) {
  return axios
    .put(`/offer_calculations/` + payload.id, { offer_calculation: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) =>
      normalizeCalculationApiError(error, "Failed to update calculation")
    );
}

export async function fetchOfferCalculation(payload) {
  // payload is only ID
  return axios
    .get(`/offer_calculations/` + payload)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function fetchOfferCalculations(controller) {
  return axios({ method: "get", url: "/offer_calculations", params: controller })
    .then((response) => {
      if (response.status === 204 || response.data == null || response.data === "") {
        return { data: [], totalCount: 0, currentCount: 0 };
      }
      if (Array.isArray(response.data)) {
        return {
          data: response.data,
          totalCount: response.data.length,
          currentCount: response.data.length,
        };
      }
      return response.data;
    })
    .catch((error) => {
      if (error?.response?.status === 204) {
        return { data: [], totalCount: 0, currentCount: 0 };
      }
      return normalizeCalculationApiError(
        error,
        "Failed to fetch calculations"
      );
    });
}

/** Soft-error envelope — keeps HTTP status for not_authorized / 404 handling. */
export function normalizeCalculationApiError(error, fallbackMessage) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return { ...data, status };
  }
  if (typeof data === "string" && data.trim()) {
    return { error: data, status };
  }
  return { error: fallbackMessage, status };
}

/** True when a soft-error envelope should reject a create/update thunk. */
export function isCalculationRequestFailed(response) {
  if (!response || typeof response !== "object") return false;
  const status = response.status ?? response.response?.status;
  if (typeof status === "number" && status >= 400) return true;
  if (response.success === false) return true;
  if (response.error) return true;
  const errors = response.errors;
  if (Array.isArray(errors) && errors.length > 0) return true;
  if (errors && typeof errors === "object" && Object.keys(errors).length > 0) {
    return true;
  }
  return false;
}

/**
 * GET /offer_calculations/:id/versions — template version history.
 * → { data: [{ id, version_number, created_by, change_note, created_at }] }
 * Soft-handles 404 while the endpoint rolls out.
 */
export async function fetchOfferCalculationVersions(id) {
  try {
    const response = await axios.get(`/offer_calculations/${id}/versions`);
    return response.data;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      return { data: [], missing: true, status: 404 };
    }
    return Promise.reject(normalizeCalculationApiError(
      error,
      "Failed to fetch calculation versions"
    ));
  }
}

/** POST /offer_calculations/:id/restore_version — body `{ version_id }`. */
export async function restoreOfferCalculationVersion(id, versionId) {
  return axios
    .post(`/offer_calculations/${id}/restore_version`, {
      version_id: versionId,
    })
    .then((response) => response.data)
    .catch((error) =>
      Promise.reject(
        normalizeCalculationApiError(
          error,
          "Failed to restore calculation version"
        )
      )
    );
}

/**
 * POST /offer_calculations/:id/save_to_template
 * body `{ template_id?, change_note?, create_new_template?, title? }`.
 */
export async function saveOfferCalculationToTemplate(id, payload = {}) {
  return axios
    .post(`/offer_calculations/${id}/save_to_template`, payload)
    .then((response) => response.data)
    .catch((error) =>
      Promise.reject(
        normalizeCalculationApiError(
          error,
          "Failed to save calculation to template"
        )
      )
    );
}

/**
 * POST /offer_calculations/:id/materialize_groups_to_offer
 * Copy template-owned structure groups onto an offer. Returns `{ id_map, groups }`.
 */
export async function materializeCalculationGroupsToOffer(id, offerId) {
  return axios
    .post(`/offer_calculations/${id}/materialize_groups_to_offer`, {
      offer_id: offerId,
    })
    .then((response) => response.data)
    .catch((error) =>
      normalizeCalculationApiError(
        error,
        "Failed to materialize calculation groups"
      )
    );
}


/**
 * POST /offer_calculations/:id/validate_formula
 * body `{ expression }` → `{ ok, error, dependencies }`.
 */
export async function validateOfferCalculationFormula(id, expression) {
  return axios
    .post(`/offer_calculations/${id}/validate_formula`, { expression })
    .then((response) => response.data)
    .catch((error) =>
      normalizeCalculationApiError(error, "Failed to validate formula")
    );
}


